/**
 * TagPills — the row's SINGLE Type pill (R-F6, reshaped by Trello #711 cl.1:
 * the Tags column became Type). A row can carry several wire tags (e.g.
 * `["bct","exported"]`) but has exactly one type — `deriveTypeTag` reads the
 * type-class tags first (precedence ai > bct > raw) and falls back to the
 * `import_format` discriminator only when they say nothing. Coloured via
 * design-system tokens from `TAG_DEF_BY_VALUE`. No derivable type renders an
 * em-dash placeholder.
 *
 * **The provenance pill also names WHO produced the file.**
 *
 * Two features arrived at that idea independently and landed on this component
 * at the same time: BCT's generator attribution (#646, `BCT · <who>`) and AI
 * Review's reviewer attribution (#669, `AI-Reviewed · <who>`). They shipped as
 * two props — `bctRunnerEmail` and `reviewerName` — doing one thing, and are
 * merged here into one, because two props for one concept is how the `bct` and
 * `ai` pills would have drifted into naming their producer differently.
 *
 * `producedBy` is read from the row's `created_by` (PR #676, migration 0031):
 * the apply worker stamps a review file's with the reviewer, `bct_worker`
 * stamps a BCT child's with the run's triggerer, and `uploader_email` stays the
 * raw ancestor's importer in both cases. So on a derived row the pill and the
 * "Imported by" column name DIFFERENT people, deliberately — the column says
 * who brought the data in, the pill says who made this particular file out of
 * it.
 *
 * It shows an email rather than a display name, because `owner_name` resolves
 * from `uploader_email` and on a derived row that is the other person. An
 * earlier revision of this component read the owner and could show the name;
 * 0031 split the columns and the name went with the wrong one.
 *
 * A row whose tag is not a provenance tag, or that carries no producer (a
 * legacy in-place review, D6), renders exactly as it always did.
 *
 * The label is `TAG_DEF_BY_VALUE[tag].label` and NOT a second constant:
 * `tagDefs.ts` is the single chip↔pill source of truth, so a chip and the pill
 * it filters for can never drift apart. (The casing argument that used to sit
 * here — the approved mock spelling `AI-Reviewed` two ways — died with the
 * label itself when Trello #711 cl.1 renamed it to `AI Review`.)
 */
import type { ImportFormat, ImportTag } from "@nu-assist/contracts";

import { PRODUCER_SEPARATOR, TAG_DEF_BY_VALUE, deriveTypeTag, typeLabel } from "./tagDefs";

/** The tags that describe HOW an import came to exist, and so can name a
 *  producer. `raw` describes a direct upload, not a derivation, and never
 *  carries a name. */
const PROVENANCE_TAGS: readonly ImportTag[] = ["bct", "ai"];

export interface TagPillsProps {
  tags: readonly ImportTag[];
  /** The row's `import_format` — the immutable provenance discriminator, used
   *  as the FALLBACK when `tags` carries no type-class tag (see
   *  `deriveTypeTag`, which explains why the tags win). */
  importFormat?: ImportFormat | null;
  /** Display name (or email fallback) of whoever PRODUCED this derived import —
   *  the reviewer for an AI Review output, the triggerer for a BCT child. Null
   *  on a directly-uploaded import, and on a legacy in-place review. */
  producedBy?: string | null;
}

export function TagPills({ tags, importFormat = null, producedBy = null }: TagPillsProps) {
  const tag = deriveTypeTag(tags, importFormat);
  if (tag === null) {
    return (
      <span className="text-text-muted" data-testid="tag-pills-empty">
        —
      </span>
    );
  }
  // Whitespace-only is treated as absent, so a pill never ends in a dangling
  // separator.
  const producer = producedBy?.trim() ? producedBy.trim() : null;
  const def = TAG_DEF_BY_VALUE[tag];
  // A format that is itself the producer (a Thrive pull: "Raw Import ·
  // Thrive") speaks only when no person does.
  const label =
    producer && PROVENANCE_TAGS.includes(tag)
      ? `${def.label}${PRODUCER_SEPARATOR}${producer}`
      : typeLabel(tag, importFormat);
  return (
    <span className="inline-flex flex-wrap gap-1" data-testid="tag-pills">
      <span
        data-tag={tag}
        // `title` + `truncate` (#646): the two-part label can be long, and a
        // narrow Type column would otherwise push the row's other pills off
        // screen. `max-w` caps it; the tooltip keeps the full text reachable.
        title={label}
        className={`inline-block max-w-[18rem] truncate rounded-full px-2 py-0.5 text-micro font-bold ${def.pillClass}`}
      >
        {label}
      </span>
    </span>
  );
}
